import type { SocialLink } from '@/components/shared/social-media';
import type {
  FooterElements,
  FooterSettings,
  FooterVariant,
  HeaderElements,
  HeaderSettings,
  HeaderVariant,
} from '@/components/slot-settings/types';
import type { BlogAuthor } from '@/lib/blog/assigned-authors';
import type { PageBlock } from '@/types/block';
import type { ButtonConfig } from '@/types/button';
import type {
  ContentModelMetadataConfig,
  Court,
  PracticeArea,
  Profile,
  Topic,
} from '@/types/shared';
import type { PageSection, PageSectionPermissions } from './page-section';
import type { CurrentUser } from './shared';

export interface VersionEditor extends Pick<CurrentUser, 'id' | 'name' | 'avatar'> {
  lastEditedAt?: string;
}

export enum SlotName {
  HEADER = 'header',
  MAIN = 'main',
  FOOTER = 'footer',
}

export type EditingSlot = SlotName.HEADER | SlotName.FOOTER;

export interface BaseSlot {
  visible: boolean;
}

export interface HeaderSlot extends BaseSlot {
  variant?: HeaderVariant;
  navigation?: RawPageFromApi[];
  elements?: HeaderElements;
  settings?: HeaderSettings;
  primaryButton?: ButtonConfig;
  secondaryButton?: ButtonConfig;
  cta?: {
    phone?: string;
    paymentLink?: string;
  };
}

export interface MainSlot extends BaseSlot {
  sections: PageSection[];
}

export interface FooterSlot extends BaseSlot {
  variant?: FooterVariant;
  navigation?: RawPageFromApi[];
  elements?: FooterElements;
  settings?: FooterSettings;
  primaryButton?: ButtonConfig;
  secondaryButton?: ButtonConfig;
  socialLinks?: SocialLink[];
  text?: string;
  socialMediaHeading?: string;
}

export interface PageSlots {
  header?: HeaderSlot;
  main: MainSlot;
  footer?: FooterSlot;
}

export enum RobotsDirective {
  NOFOLLOW = 'nofollow',
  NOSNIPPET = 'nosnippet',
  NOINDEX = 'noindex',
  NOARCHIVE = 'noarchive',
  NOIMAGEINDEX = 'noimageindex',
}

export interface PageSeoMetadata {
  titleTag?: string;
  metaDescription?: string;
  canonicalUrl?: string;
  robots?: Array<RobotsDirective>;
}

export interface PageSlotOverride {
  visible?: boolean;
}

export interface PageMetadata {
  seo?: PageSeoMetadata;
  slots?: {
    header?: PageSlotOverride;
    footer?: PageSlotOverride;
  };
}

export interface PageCustomScripts {
  headerScripts?: string;
  footerScripts?: string;
}

// Content model for special page types (blog, case_result, profile)
export interface PageContentModel {
  id?: string;
  title?: string;
  summary?: string;
  publishedAt?: string | null;
  // Editor-only working value for the publish/schedule picker; never publish it to the content model.
  editorPublishAt?: string | null;
  scheduledPublishAt?: string | null;
  topics?: Topic[];
  authors?: Profile[];
  image?: {
    id?: string;
    url?: string;
  };
  attorneys?: Profile[];
  courts?: Court[];
  practiceAreas?: PracticeArea[];
  // Profile fields
  name?: string;
  bio?: string;
  imageUrl?: string;
}

export interface PageBreadcrumb {
  title: string;
  path: string;
  pageId?: string;
}

export type AiContentGenerationStatus = 'in_progress' | 'completed' | 'failed';
export type BlogPublishIntentMode = 'immediate' | 'scheduled';

export interface Page {
  id: string;
  stableId?: string;
  path: string;
  title: string;
  pageType: PageType;
  isPublished: boolean;
  isNav: boolean;
  pinned: boolean;
  hasUnpublishedChanges: boolean;
  neverPublished?: boolean;
  navigationTitle?: string;
  url?: string;
  parentId?: string | null;
  /** Index page for blog/case_result posts (editor payload; separate from tree parentId). */
  indexPage?: Page | null;
  children?: Page[];
  metadata?: PageMetadata;
  seo?: PageSeoMetadata;
  customScripts?: PageCustomScripts;
  redirectOriginPaths?: string[];
  slots?: PageSlots;
  aiContentGenerationStatus?: AiContentGenerationStatus | null;
  contentModel?: PageContentModel;
  breadcrumbs?: PageBreadcrumb[];
  scheduledPublishAt?: string | null;
}

// Raw page shape as returned by the API (e.g. PageTreeSerializer / navigation
// responses) before normalization into the strict `Page` type. Navigation slots
// carry this loose shape, not a full `Page`.
export interface RawPageFromApi {
  id: string;
  path: string;
  title: string;
  navigationTitle?: string | null;
  pageType: string;
  isPublished?: boolean;
  isNav?: boolean;
  pinned?: boolean;
  hasUnpublishedChanges?: boolean;
  scheduledPublishAt?: string | null;
  aiContentGenerationStatus?: AiContentGenerationStatus | null;
  parentId?: string | null;
  children?: RawPageFromApi[];
}

export interface SectionStructure {
  sectionTemplateCode: string;
  sectionType?: string;
  variant?: string;
  props?: Record<string, unknown>;
  contentPayload?: Record<string, unknown>;
  blockOverrides?: Record<string, { visible?: boolean }>;
  blocks?: PageBlock[];
  permissions?: PageSectionPermissions;
}

export interface SlotConfig extends Required<BaseSlot> {}

export type SlotsConfig = {
  [K in SlotName]?: SlotConfig;
};

export interface PageVersionMetadata {
  slots: SlotsConfig;
}

export enum PageType {
  REVIEW_INDEX = 'review_index',
  BLOG_INDEX = 'blog_index',
  CASE_RESULT_INDEX = 'case_result_index',
  BLOG = 'blog',
  CASE_RESULT = 'case_result',
  HOME = 'home',
  PROFILE = 'profile',
  PRACTICE_AREA = 'practice_area',
  FAQ = 'faq',
  OFFICE = 'office',
  CONTACT = 'contact',
  LANDING_PAGE = 'landing_page',
  BLANK = 'blank',
}

/** Server-owned flags: whether each singleton index page exists for the site. */
export interface IndexPagesExist {
  blogIndex: boolean;
  caseResultIndex: boolean;
  reviewIndex: boolean;
}

export const EMPTY_INDEX_PAGES_EXIST: IndexPagesExist = {
  blogIndex: false,
  caseResultIndex: false,
  reviewIndex: false,
};

// Child page types that always belong under their respective index pages
export const CHILD_PAGE_TYPES = [PageType.BLOG, PageType.CASE_RESULT] as const;

export interface BasePageProps {
  page?: Page | null;
}

export interface BlogAiContext {
  practiceAreas: string[];
  authors: BlogAuthor[];
  topicTags: string[];
  firmName: string | null;
  serviceArea: string | null;
}

export interface PageEditorInertiaProps extends BasePageProps {
  pageId?: string;
  versionId?: string;
  latestPagesUpdatedAt?: string | null;
  blogAiContext?: BlogAiContext | null;
}

export interface FirmContext {
  firmName?: string;
  practiceAreas?: string[];
  contactPhone?: string[];
  domain?: string;
  timeZone?: string;
}

export interface SiteBrandContext {
  preferences?: SitePreferences;
  firmContext?: FirmContext;
}

export interface SiteAiProps extends SiteBrandContext {
  pageTitle?: string;
  imageStyleGuidance?: ImageStyleGuidance;
  practiceAreaSubServices?: string[];
  practiceAreaStyle?: PracticeAreaStyle;
}

export interface PageEditorSharedData {
  contentModelMetadataConfig?: ContentModelMetadataConfig;
  [key: string]: unknown;
}

export interface PracticeAreaStyle {
  mood: string;
  lighting: string;
  cameraSpecs: string;
  targetAudience?: string;
  subjectGuidance: string;
}

export interface SitePreferences {
  tone?: string[];
  customTone?: string;
  languagePreference?: 'singular' | 'plural';
  goals?: string[];
  customGoal?: string;
  missionStatement?: string;
  serviceAreas?: string[];
}

export interface ImageStyleGuidance {
  toneGuidance?: string;
  emotionalGoalGuidance?: string;
  goalGuidance?: string;
  lawFirmType?: 'solo_attorney' | 'small_law_firm' | 'unknown';
}

export interface SetPageOptions {
  resetHistory?: boolean;
}

export interface PageVersion {
  id: string;
  versionNumber: number;
  status: 'draft' | 'published';
  publishedAt: string;
  editors?: VersionEditor[];
  isLive?: boolean;
  isEditing?: boolean;
  restorable?: boolean;
  restoredFromVersionId?: string | null;
}
